// src/domains/catalog/pages/client/ClinicProfilePage.jsx
// Perfil público da clínica — layout inspirado no iFood
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { establishmentsService, usersService } from 'infrastructure/firebase';
import { useApp } from 'contexts/AppContext';
import { Avatar } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import { formatCurrency } from 'shared/utils/helpers';
import { ESTABLISHMENT_CATEGORIES } from 'shared/config/plans';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  success: '#8FC2A6',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

const DAYS_SHORT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

function Skeleton({ h = 20, w = '100%', radius = 8, mb = 0 }) {
  return (
    <div style={{ height: h, width: w, borderRadius: radius,
      background: 'linear-gradient(90deg,#F0EDF3 25%,#E8E5EC 50%,#F0EDF3 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
      marginBottom: mb }} />
  );
}

export default function ClinicProfilePage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const isMobile  = useMobile();
  const { approvedProcedures } = useApp();

  const [clinic, setClinic]           = useState(null);
  const [professionals, setPros]      = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('servicos');

  useEffect(() => {
    if (!id) return;
    Promise.all([
      establishmentsService.getById(id),
      usersService.getByRole('professional'),
    ]).then(([estab, pros]) => {
      setClinic(estab);
      setPros(pros.filter(p => p.estabelecimentoId === id && p.active !== false));
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const clinicCategory = ESTABLISHMENT_CATEGORIES.find(c => c.id === clinic?.categoria);

  // Procedimentos desta clínica: primeiro tenta por estabelecimentoId, depois por profissionais vinculados (legacy)
  const clinicProcedures = useMemo(() => {
    return approvedProcedures.filter(p => {
      if (p.estabelecimentoId) return p.estabelecimentoId === id;
      const profIds = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
      return profIds.some(pid => professionals.some(pr => pr.id === pid));
    });
  }, [approvedProcedures, id, professionals]);

  // Navega para o agendamento passando o contexto do estabelecimento
  const handleAgendar = () => navigate('/client/new-appointment', {
    state: { establishmentId: id, establishmentName: clinic?.nomeFantasia || clinic?.razaoSocial },
  });

  const handleAgendarProcedure = (proc) => navigate('/client/new-appointment', {
    state: {
      establishmentId:   id,
      establishmentName: clinic?.nomeFantasia || clinic?.razaoSocial,
      procedureId:       proc.id,
      procedureName:     proc.name,
    },
  });

  if (loading) return (
    <div style={{ fontFamily: T.sans }}>
      <Skeleton h={200} radius={0} mb={0} />
      <div style={{ padding: isMobile ? 16 : 28 }}>
        <Skeleton h={32} w="60%" mb={8} />
        <Skeleton h={16} w="40%" mb={24} />
        <Skeleton h={16} mb={8} />
        <Skeleton h={16} mb={8} />
        <Skeleton h={16} w="80%" />
      </div>
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
    </div>
  );

  if (!clinic) return (
    <div style={{ padding: 40, textAlign: 'center', fontFamily: T.sans, color: T.muted }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
      <div style={{ fontSize: 18, color: T.text, marginBottom: 8 }}>Clínica não encontrada</div>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none',
        color: T.brand, cursor: 'pointer', fontSize: 14, fontFamily: T.sans }}>
        ← Voltar ao catálogo
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: T.sans, maxWidth: 800, margin: '0 auto' }}>
      <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>

      {/* ── Banner ────────────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', height: isMobile ? 160 : 220,
        background: clinic.banner ? `url(${clinic.banner}) center/cover` : `linear-gradient(135deg, #6B5876, #B5A2D2)`,
        borderRadius: isMobile ? 0 : '16px 16px 0 0', overflow: 'hidden' }}>

        {/* Overlay escuro no rodapé do banner */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.35))' }} />

        {/* Botão voltar */}
        <button onClick={() => navigate(-1)}
          style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.9)',
            border: 'none', borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
            fontSize: 13, fontFamily: T.sans, color: T.brand, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Voltar
        </button>
      </div>

      {/* ── Cabeçalho da clínica ──────────────────────────────────────────────── */}
      <div style={{ background: '#fff', padding: isMobile ? '16px' : '20px 28px',
        borderBottom: `1px solid ${T.border}`,
        borderRadius: isMobile ? 0 : '0 0 0 0' }}>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* Avatar / logo */}
          <div style={{
            width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, flexShrink: 0,
            borderRadius: 16, overflow: 'hidden', border: `3px solid #fff`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.14)', marginTop: -32,
            background: clinic.foto ? `url(${clinic.foto}) center/cover` : T.s3,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32,
          }}>
            {!clinic.foto && (clinicCategory?.icon || '🏥')}
          </div>

          <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
            <div style={{ fontFamily: T.serif, fontSize: isMobile ? 20 : 24, color: T.text, lineHeight: 1.2 }}>
              {clinic.nomeFantasia || clinic.razaoSocial}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
              {clinicCategory && (
                <span style={{ fontSize: 12, color: T.text2, background: T.s3,
                  padding: '2px 10px', borderRadius: 12 }}>
                  {clinicCategory.icon} {clinicCategory.label}
                </span>
              )}
              {clinic.ativo !== false && (
                <span style={{ fontSize: 12, color: '#2D5F43', background: '#E7F4ED',
                  padding: '2px 10px', borderRadius: 12, fontWeight: 500 }}>
                  ● Aberto
                </span>
              )}
            </div>
          </div>

          {!isMobile && (
            <button onClick={handleAgendar}
              style={{ flexShrink: 0, padding: '10px 22px', background: T.brand, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', fontFamily: T.sans }}>
              Agendar →
            </button>
          )}
        </div>

        {/* Descrição */}
        {clinic.descricao && (
          <p style={{ fontSize: 13, color: T.text2, marginTop: 14, lineHeight: 1.6 }}>
            {clinic.descricao}
          </p>
        )}

        {/* Botão agendar mobile */}
        {isMobile && (
          <button onClick={handleAgendar}
            style={{ marginTop: 14, width: '100%', padding: '12px 0', background: T.brand, color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600,
              cursor: 'pointer', fontFamily: T.sans }}>
            Agendar agora →
          </button>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', background: '#fff', borderBottom: `1px solid ${T.border}`,
        padding: '0 16px', gap: 0 }}>
        {[
          { id: 'servicos',      label: 'Serviços' },
          { id: 'profissionais', label: 'Profissionais' },
          { id: 'info',          label: 'Informações' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '14px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 14, fontFamily: T.sans, fontWeight: activeTab === t.id ? 600 : 400,
              color: activeTab === t.id ? T.brand : T.muted,
              borderBottom: `2px solid ${activeTab === t.id ? T.brand : 'transparent'}`,
              transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Conteúdo das abas ──────────────────────────────────────────────────── */}
      <div style={{ padding: isMobile ? 16 : 24 }}>

        {/* Serviços */}
        {activeTab === 'servicos' && (
          <div>
            {clinicProcedures.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: T.muted, fontSize: 14 }}>
                Nenhum serviço cadastrado ainda.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, alignItems: 'stretch' }}>
                {clinicProcedures.map(proc => (
                  <div key={proc.id} style={{
                    background: '#fff', border: `1px solid ${T.border}`,
                    borderRadius: 14, padding: '14px 16px',
                    display: 'flex', gap: 12, alignItems: 'center',
                  }}>
                    <div style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 10,
                      background: 'linear-gradient(135deg,#B5A2D2,#C9929A)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                      {proc.emoji || '💆'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: 14, color: T.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {proc.name}
                      </div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                        {proc.duration}min · {formatCurrency(proc.price)}
                      </div>
                    </div>
                    <button onClick={() => handleAgendarProcedure(proc)}
                      style={{ flexShrink: 0, padding: '6px 14px', background: T.s3,
                        border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer',
                        fontSize: 12, fontWeight: 500, color: T.brand, fontFamily: T.sans,
                        whiteSpace: 'nowrap' }}>
                      Agendar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profissionais */}
        {activeTab === 'profissionais' && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 14 }}>
            {professionals.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0', color: T.muted, fontSize: 14 }}>
                Nenhum profissional vinculado.
              </div>
            ) : professionals.map(pro => (
              <div key={pro.id} style={{ background: '#fff', border: `1px solid ${T.border}`,
                borderRadius: 14, padding: '16px 12px', textAlign: 'center' }}>
                {pro.photo
                  ? <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 10px',
                      background: `url(${pro.photo}) center/cover` }} />
                  : <div style={{ marginBottom: 10 }}>
                      <Avatar name={pro.name} color={pro.avatarColor || 'lavender'} size="lg" />
                    </div>}
                <div style={{ fontWeight: 600, fontSize: 13, color: T.text }}>{pro.name}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
                  {pro.descricaoProfissional?.slice(0, 50) || pro.specialty || 'Profissional'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Informações */}
        {activeTab === 'info' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {clinic.telefone && (
              <div style={{ display: 'flex', gap: 12, padding: '14px 16px', background: '#fff',
                border: `1px solid ${T.border}`, borderRadius: 12 }}>
                <span style={{ fontSize: 20 }}>📞</span>
                <div>
                  <div style={{ fontSize: 12, color: T.muted }}>Telefone</div>
                  <div style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{clinic.telefone}</div>
                </div>
              </div>
            )}
            {clinic.email && (
              <div style={{ display: 'flex', gap: 12, padding: '14px 16px', background: '#fff',
                border: `1px solid ${T.border}`, borderRadius: 12 }}>
                <span style={{ fontSize: 20 }}>✉️</span>
                <div>
                  <div style={{ fontSize: 12, color: T.muted }}>E-mail</div>
                  <div style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{clinic.email}</div>
                </div>
              </div>
            )}
            <div style={{ padding: '16px', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12 }}>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 1 }}>
                Horário de atendimento
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {DAYS_SHORT.map((day, i) => {
                  const works = (clinic.workDays || [1,2,3,4,5,6]).includes(i);
                  return (
                    <div key={i} style={{ padding: '4px 12px', borderRadius: 20,
                      background: works ? T.s3 : T.s2,
                      color: works ? T.brand : T.muted, fontSize: 12,
                      fontWeight: works ? 600 : 400, border: `1px solid ${works ? T.border : 'transparent'}` }}>
                      {day}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
